import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { validateClaimAgainstUserStory } from '../../utils/userStoryValidator';

/**
 * BenefitPreview Component
 * 
 * Displays a read-only preview of the calculated benefit amount based on claim details.
 * This replaces the manual benefit amount input to prevent validation conflicts.
 * Enhanced with user story validation compliance.
 */
const BenefitPreview = ({ 
  claimType, 
  sumAssured, 
  conditionSeverityStage, 
  disabilityType, 
  estimatedPaymentDuration 
}) => {
  const [calculatedBenefit, setCalculatedBenefit] = useState(null);
  const [calculationDetails, setCalculationDetails] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);

  const calculateBenefitPreview = useCallback(async () => {
    let benefit = 0;
    let details = { formula: '', breakdown: '' };
    let warnings = [];

    const normalizedClaimType = claimType?.toUpperCase();
    const numericSumAssured = parseFloat(sumAssured);

    // Validate against user story requirements
    try {
      const claimData = {
        claimType,
        sumAssured: numericSumAssured,
        conditionSeverityStage,
        disabilityType,
        estimatedPaymentDuration
      };
      
      const validation = await validateClaimAgainstUserStory(claimData);
      if (!validation.isValid) {
        warnings = validation.errors;
      }
    } catch (error) {
      warnings.push('Validation check failed: ' + error.message);
    }

    switch (normalizedClaimType) {
      case 'DEATH':
        benefit = numericSumAssured;
        details = {
          formula: '100% of Sum Assured',
          breakdown: `₵${numericSumAssured.toLocaleString()} × 100% = ₵${benefit.toLocaleString()}`,
          explanation: 'Death claims pay the full sum assured amount'
        };
        break;

      case 'CRITICAL_ILLNESS': {
        const stagePayouts = {
          'EARLY': 25,
          'INTERMEDIATE': 50,
          'ADVANCED': 75,
          'TERMINAL': 100
        };
        
        const percentage = stagePayouts[conditionSeverityStage?.toUpperCase()] || 50;
        benefit = Math.round((numericSumAssured * percentage) / 100);
        
        details = {
          formula: `${percentage}% of Sum Assured`,
          breakdown: `₵${numericSumAssured.toLocaleString()} × ${percentage}% = ₵${benefit.toLocaleString()}`,
          explanation: `${conditionSeverityStage || 'Intermediate'} stage critical illness payout`
        };
        
        // Add user story compliance note
        if (!conditionSeverityStage) {
          warnings.push('Severity stage not specified - using intermediate (50%) as default');
        }
        break;
      }

      case 'DISABILITY':
        if (disabilityType?.toUpperCase() === 'PTD') {
          benefit = numericSumAssured;
          details = {
            formula: '100% of Sum Assured (PTD)',
            breakdown: `₵${numericSumAssured.toLocaleString()} × 100% = ₵${benefit.toLocaleString()}`,
            explanation: 'Permanent Total Disability pays full sum assured'
          };
        } else {
          // TTD calculation
          const duration = Math.min(estimatedPaymentDuration || 3, 6);
          const monthlyAmount = Math.round((numericSumAssured * 10) / 100);
          benefit = monthlyAmount * duration;
          
          details = {
            formula: `10% per month × ${duration} months`,
            breakdown: `(₵${numericSumAssured.toLocaleString()} × 10%) × ${duration} months = ₵${benefit.toLocaleString()}`,
            explanation: `Temporary Total Disability pays 10% monthly for ${duration} months`
          };
          
          if (duration > 6) {
            warnings.push('TTD duration exceeds 6-month limit per user story');
          }
        }
        break;

      default:
        benefit = 0;
        details = {
          formula: 'Unknown claim type',
          breakdown: 'Please select a valid claim type',
          explanation: ''
        };
    }

    setCalculatedBenefit(benefit);
    setCalculationDetails(details);
    setValidationWarnings(warnings);
  }, [claimType, sumAssured, conditionSeverityStage, disabilityType, estimatedPaymentDuration]);

  useEffect(() => {
    // Only calculate if we have the minimum required fields
    if (!claimType || !sumAssured || sumAssured <= 0) {
      setCalculatedBenefit(null);
      setCalculationDetails(null);
      return;
    }

    calculateBenefitPreview();
  }, [claimType, sumAssured, conditionSeverityStage, disabilityType, estimatedPaymentDuration, calculateBenefitPreview]);

  // Don't render if no claim type or sum assured
  if (!claimType || !sumAssured) {
    return null;
  }

  return (
    <div className="space-y-3 p-4 bg-green-50 border border-green-200 rounded-lg">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-green-800">
          Calculated Benefit Amount
        </label>
        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
          Auto-calculated
        </span>
      </div>
      
      <div className="text-2xl font-bold text-green-700">
        {calculatedBenefit !== null ? `₵ ${calculatedBenefit.toLocaleString()}` : 'Calculating...'}
      </div>
      
      {calculationDetails && (
        <div className="space-y-2 text-sm text-green-700">
          <div>
            <span className="font-medium">Formula:</span> {calculationDetails.formula}
          </div>
          <div>
            <span className="font-medium">Calculation:</span> {calculationDetails.breakdown}
          </div>
          {calculationDetails.explanation && (
            <div className="text-xs text-green-600 italic">
              {calculationDetails.explanation}
            </div>
          )}
        </div>
      )}
      
      {/* Display validation warnings */}
      {validationWarnings.length > 0 && (
        <div className="space-y-1 p-3 bg-amber-50 border border-amber-200 rounded">
          <div className="font-medium text-amber-800 text-sm">User Story Compliance Warnings:</div>
          {validationWarnings.map((warning, index) => (
            <div key={index} className="text-xs text-amber-700 flex items-start">
              <svg className="w-3 h-3 mt-0.5 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {warning}
            </div>
          ))}
        </div>
      )}
      
      <div className="text-xs text-green-600 border-t border-green-200 pt-2">
        <strong>Note:</strong> This amount is automatically calculated based on your claim details. 
        No manual entry required. All calculations follow user story requirements.
      </div>
    </div>
  );
};

BenefitPreview.propTypes = {
  claimType: PropTypes.string,
  sumAssured: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  conditionSeverityStage: PropTypes.string,
  disabilityType: PropTypes.string,
  estimatedPaymentDuration: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default BenefitPreview;
